import { clearCart, clearPromoCodes } from "../store/cartSlice";
import { setOrderDetails } from "../store/checkoutSlice";
import { resetCustomizer } from "../store/customizerSlice";
import { fbqTrack } from "./eventsTracking/fbq";
import { gtagEvent } from "./eventsTracking/gtag";
import { trackCustomEvent } from "./eventsTracking/trackCustomEvent";
import { getImageFromDB, resetIndexedDB } from "./indexedDB";
import { retryRequest } from "./retryRequest";
import { scrollToTop } from "./scrollToTop";

export const checkoutFinalize = async ({
  cartItems,
  transformedCartItems,
  setCheckoutError,
  values,
  comment,
  affiliate,
  latestPromoCodes,
  dispatch,
  navigate,
  paymentMethodId,
  customerId
}) => {
  try {
    const customer_first =  values.firstName;
    const customer_last =  values.surname;
    const customer_email =  values.email;
    const customer_phone =  values.phone;
    const shipping_address_1 =  values.address;
    const shipping_address_2 =  values.address2;
    const shipping_city =  values.city;
    const shipping_state =  values.state;
    const shipping_postcode =  values.postalCode;

    // Get image slugs
    const allAttachmentSlugs = new Set();
    cartItems.forEach((item) => {
      if (item.imageId) allAttachmentSlugs.add(item.imageId);
      if (Array.isArray(item.uploadedImageIds)) {
        item.uploadedImageIds.forEach((id) => allAttachmentSlugs.add(id));
      }
    });

    // Fetch images
    const attachments = await Promise.all(
      Array.from(allAttachmentSlugs).map(async (slug) => {
        const blob =
          (await getImageFromDB("resultImages", slug)) ||
          (await getImageFromDB("uploadedImages", slug));
        if (!blob) return null;
        return {
          slug,
          file: new File([blob], `${slug}.png`, { type: "image/png" }),
        };
      })
    );
    const validAttachments = attachments.filter(Boolean);

    // JSON payload
    const jsonPayload = {
      customer_first,
      customer_last,
      customer_email,
      customer_phone,
      shipping_address_1,
      shipping_address_2,
      shipping_city,
      shipping_state,
      shipping_postcode,
      order_notes: comment || "",
      discount_codes: latestPromoCodes.map((p) => p.code),
      stripe_customer_id: customerId,
      stripe_payment_method_id: paymentMethodId,
      cart_id: localStorage.getItem('cart_id'),
      products: transformedCartItems.map((item) => {
        if (item.type === 'bundle') {
          return {
            type: 'bundle',
            bundleId: item.bundleId,
            quantity: item.quantity,
            products: item.products.map((product) => ({
              id: product.id,
              sku: product.sku,
              label_template: product.labelTemplate,
              pet_name: product.petName || null,
              quantity: product.quantity,
              subscription: product.subscription || false,
              file_custom_label: product.imageId || null,
              file_original_photos: product.uploadedImageIds || [],
              image_metadata: product.imageMetadata || null,
              bundleId: product.bundleId
            }))
          };
        } else {
          return {
            id: item.id,
            sku: item.sku,
            label_template: item.labelTemplate,
            pet_name: item.petName || null,
            quantity: item.quantity,
            subscription: item.subscription || false,
            file_custom_label: item.imageId || null,
            file_original_photos: item.uploadedImageIds || [],
            image_metadata: item.imageMetadata || null,
          };
        }
      }),
      ref: affiliate?.code || null,
      attachments: validAttachments.map(({ slug }) => ({
        slug,
        file: `ATTACHMENT_${slug}`,
      })),
    };

    // FormData
    const formData = new FormData();
    formData.append(
      "payload",
      new Blob([JSON.stringify(jsonPayload)], { type: "application/json" })
    );
    validAttachments.forEach(({ slug, file }) => {
      formData.append(`ATTACHMENT_${slug}`, file);
    });

    // Checkout call
    const response = await retryRequest(() =>
      fetch("https://api.treatink.com/functions/v1/complete-checkout", {
        method: "POST",
        body: formData,
      })
    );
    const result = await response.json();

    if (!response.ok || result.transaction_status !== "success") {
      setCheckoutError([result.error || "Checkout failed. Please try again."]);
      return;
    }

    // Success actions
    dispatch(
      setOrderDetails({
        total: result.transaction_total,
        cartItems,
      })
    );

    // Events for marketing
    trackCustomEvent('purchase_success', {
      total: result.transaction_total,
      products: cartItems.map(item => ({
        sku: item.sku,
        price: item.price,
        title: item.name,
        quantity: item.quantity,
        flavor: item.tag || null,
        subscription: item.subscription || false,
      })),
    }, affiliate?.code || null);
    fbqTrack('Purchase', {
      contents: cartItems.map(item => ({
        id: item.sku,
        quantity: item.quantity,
        item_price: item.price,
        content_name: item.name,
        subscription: item.subscription || false,
        flavor: item.tag || null
      })),
      content_type: 'product',
      content_ids: cartItems.map(item => item.sku),
      value: result.transaction_total,
      currency: 'USD',
      ref: affiliate?.code || null
    });
    gtagEvent('purchase', {
      transaction_id: result.order_id || null,
      currency: 'USD',
      value: result.transaction_total,
      items: cartItems.map(item => ({
        item_id: item.sku,
        item_name: item.name,
        quantity: item.quantity,
        price: item.price,
        subscription: item.subscription || false,
        flavor: item.tag || null
      })),
      ref: affiliate?.code || null
    });

    resetIndexedDB('resultImages', cartItems);
    resetIndexedDB('onProductImages', cartItems);
    resetIndexedDB('uploadedImages', cartItems);

    dispatch(resetCustomizer());
    dispatch(clearCart());
    dispatch(clearPromoCodes());

    localStorage.removeItem('restored_cart_id');
    localStorage.removeItem('cart_id');

    setCheckoutError([]);
    navigate('/thank-you');
    scrollToTop();
  } catch (error) {
    console.error(error);
    setCheckoutError([error.message || "Unexpected error."]);
  }
};
